import Editor, { OnMount } from "@monaco-editor/react";
import { useTheme } from "next-themes";
import { useEffect, useRef } from "react";

interface CodeEditorProps {
  code: string;
  onChange: (value: string | undefined) => void;
  height?: string;
  placeholder?: string;
}

export const CodeEditor = ({ code, onChange, height = "200px", placeholder = "Paste your VBA macro code here..." }: CodeEditorProps) => {
  const { theme } = useTheme();
  const monacoRef = useRef<any>(null);
  const isEmpty = !code;
  
  const handleEditorDidMount: OnMount = (editor, monaco) => {
    monacoRef.current = monaco;
    
    // Define custom light theme with light gray background
    monaco.editor.defineTheme('card-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#f5f5f5',
      }
    });
    
    // Define custom dark theme matching card background
    monaco.editor.defineTheme('card-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#151820',
      }
    });
    
    // Apply the appropriate theme
    monaco.editor.setTheme(theme === 'dark' ? 'card-dark' : 'card-light');
  };
  
  // Update theme when it changes
  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(theme === 'dark' ? 'card-dark' : 'card-light');
    }
  }, [theme]);
  
  return (
    <div className="rounded-xl overflow-hidden border border-border relative">
      {isEmpty && (
        <div className="absolute top-4 left-6 text-muted-foreground text-sm pointer-events-none z-10">
          {placeholder}
        </div>
      )}
      <Editor
        height={height}
        defaultLanguage="vb"
        value={code}
        onChange={onChange}
        onMount={handleEditorDidMount}
        theme={theme === "dark" ? "card-dark" : "card-light"}
        options={{
          readOnly: false,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 14,
          lineHeight: 22,
          padding: { top: 16, bottom: 16, left: 0 },
          lineNumbers: "off",
          renderLineHighlight: "none",
          wordWrap: "on",
          scrollbar: {
            alwaysConsumeMouseWheel: false,
          },
          quickSuggestions: false,
          suggestOnTriggerCharacters: false,
          acceptSuggestionOnEnter: "off",
          tabCompletion: "off",
          wordBasedSuggestions: "off",
          parameterHints: { enabled: false },
          inlineSuggest: { enabled: false },
          ghostText: { enabled: false },
        }}
      />
    </div>
  );
};
